import { Button, ModalHeader } from '@ui'
import { Box, Layer, Text } from 'grommet'
import { useEffect } from 'react'
import { VisitFacts, patientSummaryLanguageName } from '../../../api/patientSummary'
import type { ClinicBranding } from '../../../utils/clinicBranding'
import { createScopedPrint } from '../../../utils/printScoped'
import { VISIT_SUMMARY_PRINT_NAMES } from '../../../utils/visitSummaryPrint'
import ClinicLetterhead from './ClinicLetterhead'
import VisitSummaryDocument from './index'

/** What the modal prints — a completed summary, from whichever endpoint the caller may use. */
export type VisitSummaryPrintData = {
  facts: VisitFacts
  instructionsHtml: string | null
  translationHtml: string | null
  language: string | null
  /** "Note from your doctor" — printed last before the signature when present. */
  doctorNote?: string | null
}

type Props = {
  /** Undefined while loading, after an error, or when there's nothing to show. */
  data?: VisitSummaryPrintData
  isLoading: boolean
  isError: boolean
  branding: ClinicBranding
  onClose: () => void
  title: string
  loadingText: string
  errorText: string
}

// One instance for every mount — only the data changes. Print scoping: utils/printScoped.ts; the
// document's A4 page setup is scoped to this print name (utils/visitSummaryPrint.ts).
const scopedPrint = createScopedPrint(VISIT_SUMMARY_PRINT_NAMES.modal)

/**
 * A completed visit summary in a side modal with a big Print / Save as PDF button — the data source
 * is the caller's: the patient portal (its own snapshot endpoint) and reception at check-out (the
 * staff endpoint, which lets `patient`-permission staff read COMPLETED summaries only).
 */
export default function VisitSummaryPrintModal({
  data,
  isLoading,
  isError,
  branding,
  onClose,
  title,
  loadingText,
  errorText
}: Props) {
  // Leaving the modal (closing it, or navigating away) must not leave summary-only printing on.
  useEffect(() => () => scopedPrint.end(), [])

  return (
    <Layer margin={{ vertical: '4px' }} modal onClickOutside={onClose} onEsc={onClose} position="right" style={{}}>
      <Box fill style={{ height: '100vh', width: 'min(700px, calc(100vw - 1.5rem))' }}>
        <style>{scopedPrint.css}</style>
        <ModalHeader onClose={onClose} title={title} />
        <Box overflow="auto" pad="medium">
          {isLoading && <Text size="14px">{loadingText}</Text>}
          {!isLoading && isError && <Text size="14px">{errorText}</Text>}
          {data && (
            <div className="flex flex-col gap-4">
              <div className="print:hidden">
                <Button kinds="primary" label="Print / Save as PDF" onClick={scopedPrint.print} size="lg" />
              </div>
              <div className={`${scopedPrint.printRootClass} rounded-lg border border-[#E5E5E5]`}>
                <VisitSummaryDocument
                  clinicPhone={branding.phone}
                  doctorNote={data.doctorNote}
                  facts={data.facts}
                  instructionsHtml={data.instructionsHtml ?? ''}
                  language={data.language}
                  languageName={patientSummaryLanguageName(data.language)}
                  letterhead={<ClinicLetterhead {...branding} />}
                  translationHtml={data.translationHtml}
                />
              </div>
            </div>
          )}
        </Box>
      </Box>
    </Layer>
  )
}
